import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  MessageOutlined,
  WhatsAppOutlined,
  RightOutlined,
} from '@ant-design/icons';

import SecurityIcon from '@portal/assets/svg/ic_new_security.svg?react';

import { AuthActions, DeviceAction } from '@portal/actions';
import { requestSms } from '@portal/actions/user';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import Loading from '@portal/components/Loading/Loading';
import Spinner from '@portal/components/Spinner/Spinner';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { TokenMethod } from '@portal/enum/device';
import useReduxState from '@portal/hooks/useReduxState';
import { translate } from '@portal/services/i18n';
import NavigationService from '@portal/services/navigation';

import './TokenChannelSelection.less';

const TokenChannelSelection = () => {
  const dispatch = useDispatch();
  const [loadingMethod, setLoadingMethod] = useState<TokenMethod | null>(null);

  const {
    loading: { amount: isLoading },
    theme: { currentTheme },
    device: { lastNumbers },
  } = useReduxState();

  const handleSelectChannel = async (method: TokenMethod) => {
    setLoadingMethod(method);
    try {
      await requestSms(method);
      NavigationService.navigateWithState('/solicitar-sms', { method });
    } catch (error) {
      setLoadingMethod(null);
      throw error;
    } finally {
      setLoadingMethod(null);
    }
  };

  const handleCancel = () => {
    dispatch(AuthActions.logout());
    NavigationService.navigate('/');
  };

  useEffect(() => {
    const fetchRegisterInfo = async () => {
      try {
        await dispatch(DeviceAction.getRegisterInformation());
      } catch {
        NavigationService.navigate('/');
      }
    };

    fetchRegisterInfo();
  }, [dispatch]);

  return (
    <>
      {isLoading > 0 && <Loading />}

      <div
        className="token-channel-selection"
        style={{ background: currentTheme.background }}
      >
        <div className="token-channel-selection__form">
          <div className="token-channel-selection__form__icon-security">
            <div className="token-channel-selection__form__icon-security__circle">
              <img
                src="assets/svg/lock.svg"
                alt={translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SECURE')}
                className="token-channel-selection__form__icon-security__icon"
              />
            </div>
          </div>
          <ThemedText
            variant="h2"
            className="token-channel-selection__form__header"
          >
            {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.TITLE')}
          </ThemedText>
          <ThemedText
            variant="body"
            className="token-channel-selection__form__subtitle"
          >
            {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.DESCRIPTION')}
          </ThemedText>
          <div className="token-channel-selection__form__secure-badge">
            <SecurityIcon
              fill={currentTheme.text}
              className="token-channel-selection__form__secure-badge__icon"
            />
            <span>
              {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SECURE')}
            </span>
          </div>
          <div className="token-channel-selection__form__options">
            <div
              className={`token-channel-selection__form__option-card ${
                loadingMethod === TokenMethod.SMS
                  ? 'token-channel-selection__form__option-card--loading'
                  : ''
              }`}
              onClick={() => handleSelectChannel(TokenMethod.SMS)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectChannel(TokenMethod.SMS);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={translate(
                'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SMS_BUTTON'
              )}
              data-testid="token-channel-selection_sms"
            >
              <div className="token-channel-selection__form__option-card__icon token-channel-selection__form__option-card__icon--sms">
                <MessageOutlined />
              </div>
              <div className="token-channel-selection__form__option-card__content">
                <div className="token-channel-selection__form__option-card__title">
                  {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SMS_BUTTON')}
                </div>
                <div className="token-channel-selection__form__option-card__description">
                  {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SUBTITLE', {
                    lastNumbers: lastNumbers || '',
                  })}
                </div>
              </div>
              {loadingMethod === TokenMethod.SMS ? (
                <Spinner size="small" />
              ) : (
                <RightOutlined className="token-channel-selection__form__option-card__arrow" />
              )}
            </div>
            <div
              className={`token-channel-selection__form__option-card ${
                loadingMethod === TokenMethod.WHATSAPP
                  ? 'token-channel-selection__form__option-card--loading'
                  : ''
              }`}
              onClick={() => handleSelectChannel(TokenMethod.WHATSAPP)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectChannel(TokenMethod.WHATSAPP);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={translate(
                'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.WHATSAPP_BUTTON'
              )}
              data-testid="token-channel-selection_whatsapp"
            >
              <div className="token-channel-selection__form__option-card__icon token-channel-selection__form__option-card__icon--whatsapp">
                <WhatsAppOutlined />
              </div>
              <div className="token-channel-selection__form__option-card__content">
                <div className="token-channel-selection__form__option-card__title">
                  {translate(
                    'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.WHATSAPP_BUTTON'
                  )}
                </div>
                <div className="token-channel-selection__form__option-card__description">
                  {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SUBTITLE', {
                    lastNumbers: lastNumbers || '',
                  })}
                </div>
              </div>
              {loadingMethod === TokenMethod.WHATSAPP ? (
                <Spinner size="small" />
              ) : (
                <RightOutlined className="token-channel-selection__form__option-card__arrow" />
              )}
            </div>
          </div>
          <AdvancedButton
            variant="outlined"
            fullWidth
            text={translate('COMPONENTS.ADVANCED_BUTTON.CANCEL')}
            onClick={handleCancel}
            className="token-channel-selection__form__cancel-button"
          />
        </div>
      </div>
    </>
  );
};

export default TokenChannelSelection;
